import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiUpload, FiAlertCircle, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { ManagerLayout } from '../../components/layout';
import { Card, Button, Loading, AlertModal, ConfirmDialog } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { getCompanyById, updateCompany, toggleCompanyStatus } from '../../firebase/companies';
import { uploadCompanyLogo } from '../../firebase/storage';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const CompanySettings = () => {
  const navigate = useNavigate();
  const { user, managerCompanyId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, title: '', type: 'warning' });
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    slug: '',
    description: '',
    
    // Location
    location: '',
    serviceAreas: [''],
    
    // Contact
    phone: '',
    email: '',
    whatsapp: '',
    
    // Business Details
    gstNumber: '',
    
    // Operating Hours
    operatingHours: {
      open: '09:00',
      close: '21:00',
      closedDays: []
    },
    
    // Policies
    termsAndConditions: '',
    cancellationPolicy: '',
    
    // Pricing Config
    serviceChargePercent: 5,
    serviceChargeEnabled: true,
    gstPercent: 18,
    gstEnabled: true,
    
    // Status
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (managerCompanyId) {
      loadCompany();
    }
  }, [managerCompanyId]);

  const loadCompany = async () => {
    if (!managerCompanyId) return;
    
    setLoading(true);
    try {
      const result = await getCompanyById(managerCompanyId);
      if (result.company) {
        const company = result.company;
        setFormData({
          name: company.name || '',
          slug: company.slug || '',
          description: company.description || '',
          location: company.location || '',
          serviceAreas: company.serviceAreas && company.serviceAreas.length > 0 
            ? company.serviceAreas 
            : [''],
          phone: company.phone || '',
          email: company.email || '',
          whatsapp: company.whatsapp || '',
          gstNumber: company.gstNumber || '',
          operatingHours: company.operatingHours || {
            open: '09:00',
            close: '21:00',
            closedDays: []
          },
          termsAndConditions: company.termsAndConditions || '',
          cancellationPolicy: company.cancellationPolicy || '',
          serviceChargePercent: company.serviceChargePercent || 5,
          serviceChargeEnabled: company.serviceChargeEnabled !== false,
          gstPercent: company.gstPercent || 18,
          gstEnabled: company.gstEnabled !== false,
          status: company.status || 'active'
        });
        
        if (company.logo) {
          setLogoPreview(company.logo);
        }
      }
    } catch (error) {
      console.error('Error loading company:', error);
      setErrors({ submit: 'Failed to load company details' });
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle service areas
  const handleServiceAreaChange = (index, value) => {
    const newAreas = [...formData.serviceAreas];
    newAreas[index] = value;
    setFormData(prev => ({ ...prev, serviceAreas: newAreas }));
  };

  const addServiceArea = () => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: [...prev.serviceAreas, '']
    }));
  };

  const removeServiceArea = (index) => {
    const newAreas = formData.serviceAreas.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      serviceAreas: newAreas.length > 0 ? newAreas : ['']
    }));
  };

  // Handle closed days
  const handleClosedDayToggle = (day) => {
    const closedDays = formData.operatingHours.closedDays || [];
    const newClosedDays = closedDays.includes(day)
      ? closedDays.filter(d => d !== day)
      : [...closedDays, day];
    
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        closedDays: newClosedDays
      }
    }));
  };

  // Handle logo upload
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }
    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.location || !formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaving(true);
    setErrors({});
    
    try {
      // Upload logo if changed
      let logoUrl = formData.logo || logoPreview;
      if (logoFile) {
        const uploadResult = await uploadCompanyLogo(logoFile, managerCompanyId);
        if (uploadResult.url) {
          logoUrl = uploadResult.url;
        }
      }
      
      // Prepare update data (exclude slug and status - these are admin-only)
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        location: formData.location.trim(),
        serviceAreas: formData.serviceAreas.filter(area => area.trim()).map(area => area.trim()),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        whatsapp: formData.whatsapp?.trim() || '',
        gstNumber: formData.gstNumber?.trim() || '',
        operatingHours: formData.operatingHours,
        termsAndConditions: formData.termsAndConditions.trim(),
        cancellationPolicy: formData.cancellationPolicy.trim(),
        serviceChargePercent: Number(formData.serviceChargePercent) || 0,
        serviceChargeEnabled: formData.serviceChargeEnabled,
        gstPercent: Number(formData.gstPercent) || 0,
        gstEnabled: formData.gstEnabled
      };
      
      if (logoUrl) {
        updateData.logo = logoUrl;
      }
      
      const result = await updateCompany(managerCompanyId, updateData);
      
      if (result.error) {
        setErrors({ submit: result.error });
        setSaving(false);
        return;
      }
      
      // Show success message
      setAlert({
        isOpen: true,
        type: 'success',
        title: 'Settings Updated',
        message: 'Company settings updated successfully!'
      });
      loadCompany(); // Reload to get latest data
      
    } catch (error) {
      console.error('Error updating company:', error);
      setErrors({ submit: 'Failed to update company settings. Please try again.' });
      setSaving(false);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Change Company Status',
      message: `Are you sure you want to ${formData.status === 'active' ? 'deactivate' : 'activate'} your company?`,
      type: 'warning',
      onConfirm: async () => {
        try {
          const newStatus = formData.status === 'active' ? 'inactive' : 'active';
          const result = await toggleCompanyStatus(managerCompanyId, newStatus);
          
          if (result.error) {
            setAlert({
              isOpen: true,
              type: 'error',
              title: 'Update Failed',
              message: result.error || 'Failed to update status. Please try again.'
            });
          } else {
            setFormData(prev => ({ ...prev, status: newStatus }));
            setAlert({
              isOpen: true,
              type: 'success',
              title: 'Status Updated',
              message: `Company ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`
            });
          }
        } catch (error) {
          console.error('Error updating status:', error);
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Update Failed',
            message: 'Failed to update status. Please try again.'
          });
        }
      }
    });
  };

  if (loading) {
    return (
      <ManagerLayout title="Company Settings">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </ManagerLayout>
    );
  }

  return (
    <ManagerLayout title="Company Settings">
      <form onSubmit={handleSubmit}>
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <FiAlertCircle className="text-red-500 flex-shrink-0" size={20} />
              <p className="text-red-700">{errors.submit}</p>
            </div>
          )}

          {/* Status Toggle */}
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Company Status</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {formData.status === 'active' 
                    ? 'Your company is currently active and visible to customers'
                    : 'Your company is currently inactive and hidden from customers'
                  }
                </p>
              </div>
              <button
                type="button"
                onClick={handleStatusToggle}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  formData.status === 'active'
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {formData.status === 'active' ? (
                  <>
                    <FiToggleRight size={20} />
                    Active
                  </>
                ) : (
                  <>
                    <FiToggleLeft size={20} />
                    Inactive
                  </>
                )}
              </button>
            </div>
          </Card>

          {/* Logo */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Logo</h3>
            
            <div className="flex items-center gap-6">
              {logoPreview && (
                <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
                  <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" />
                </div>
              )}
              
              <div>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 transition-colors">
                  <FiUpload size={18} />
                  {logoPreview ? 'Change Logo' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  Recommended: Square image, at least 200x200px
                </p>
              </div>
            </div>
          </Card>

          {/* Basic Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe your car rental business..."
                />
              </div>
            </div>
          </Card>

          {/* Location */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Location</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Location *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Mumbai, Maharashtra"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.location ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Areas
                </label>
                {formData.serviceAreas.map((area, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={area}
                      onChange={(e) => handleServiceAreaChange(index, e.target.value)}
                      placeholder="e.g., Mumbai"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {formData.serviceAreas.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeServiceArea(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addServiceArea}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  + Add Service Area
                </button>
              </div>
            </div>
          </Card>

          {/* Contact Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="contact@company.com"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number
                </label>
                <input
                  type="text"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  placeholder="27AAAAA0000A1Z5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </Card>

          {/* Operating Hours */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Operating Hours</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opening Time
                </label>
                <input
                  type="time"
                  name="operatingHours.open"
                  value={formData.operatingHours.open}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closing Time
                </label>
                <input
                  type="time"
                  name="operatingHours.close"
                  value={formData.operatingHours.close}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Closed Days
              </label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleClosedDayToggle(day)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      formData.operatingHours.closedDays?.includes(day)
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Pricing Configuration */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Configuration</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Charge
                  </label>
                  <p className="text-sm text-gray-500">Additional service charge percentage</p>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    name="serviceChargePercent"
                    value={formData.serviceChargePercent}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <span className="text-gray-700">%</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="serviceChargeEnabled"
                      checked={formData.serviceChargeEnabled}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700">Enabled</span>
                  </label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GST
                  </label>
                  <p className="text-sm text-gray-500">GST percentage</p>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    name="gstPercent"
                    value={formData.gstPercent}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <span className="text-gray-700">%</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="gstEnabled"
                      checked={formData.gstEnabled}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700">Enabled</span>
                  </label>
                </div>
              </div>
            </div>
          </Card>

          {/* Policies */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Policies</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Terms and Conditions
                </label>
                <textarea
                  name="termsAndConditions"
                  value={formData.termsAndConditions}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your terms and conditions..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cancellation Policy
                </label>
                <textarea
                  name="cancellationPolicy"
                  value={formData.cancellationPolicy}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your cancellation policy..."
                />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/manager')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2"
            >
              {saving ? <Loading size="sm" /> : <FiSave size={18} />}
              Save Changes
            </Button>
          </div>
        </div>
      </form>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />
    </ManagerLayout>
  );
};

export default CompanySettings;
